import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import {
  LayoutDashboard,
  GraduationCap,
  FileText,
  FolderOpen,
  Users,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  BookOpen,
  ClipboardList,
  Shield,
  Menu,
  Award,
  Upload,
  MessageSquare,
  Megaphone,
  FileCheck,
  UserCheck,
  CreditCard,
  Target,
  Video,
  Globe,
  Gift,
  Calendar,
  Download,
  Cloud,
  Headphones,
  Layers,
  CalendarDays,
  Images,
  UserPlus,
  Database,
  Newspaper,
  ShieldAlert,
  Activity,
  AlertOctagon,
  Mail,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';

const delegateMenuItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/dashboard' },
  { icon: GraduationCap, label: 'My Courses', path: '/courses' },
  { icon: Upload, label: 'My Coursework', path: '/my-coursework' },
  { icon: FolderOpen, label: 'My Documents', path: '/my-documents' },
  { icon: FileText, label: 'Assignments', path: '/assignments' },
  { icon: MessageSquare, label: 'Discussions', path: '/discussions' },
  { icon: CreditCard, label: 'Payments', path: '/payments' },
  { icon: Award, label: 'Certificates', path: '/certificates' },
  { icon: Settings, label: 'Settings', path: '/settings' },
];

const adminMenuItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/admin' },
  { icon: Users, label: 'Users', path: '/admin/users' },
  { icon: BookOpen, label: 'Courses', path: '/admin/courses' },
  { icon: GraduationCap, label: 'Cohorts', path: '/admin/cohorts' },
  { icon: Layers, label: 'Programmes', path: '/admin/programmes' },
  { icon: CalendarDays, label: 'Events', path: '/admin/events' },
  { icon: Images, label: 'Gallery', path: '/admin/gallery' },
  { icon: Newspaper, label: 'Blogs', path: '/admin/blogs' },
  { icon: UserPlus, label: 'Waitlist', path: '/admin/waitlist' },
  { icon: ClipboardList, label: 'Assignments', path: '/admin/assignments' },
  { icon: Calendar, label: 'Course Schedule', path: '/admin/course-schedule' },
  { icon: CreditCard, label: 'Payments & VAT', path: '/admin/payments' },
  { icon: Gift, label: 'Referrals', path: '/admin/referrals' },
  { icon: Target, label: 'Lead Capture', path: '/admin/leads' },
  { icon: MessageSquare, label: 'Email Sequences', path: '/admin/email-sequences' },
  { icon: Video, label: 'Testimonials', path: '/admin/testimonials' },
  { icon: Megaphone, label: 'Announcements', path: '/admin/announcements' },
  { icon: Headphones, label: 'Support Tickets', path: '/admin/support' },
  { icon: Globe, label: 'Website Settings', path: '/admin/website-settings' },
  { icon: Cloud, label: 'Storage', path: '/admin/storage' },
  { icon: Download, label: 'Data Export', path: '/admin/data-export' },
  { icon: Database, label: 'DB Mirroring', path: '/admin/database-mirroring' },
  { icon: Activity, label: 'User Activity', path: '/admin/user-activity' },
  { icon: Mail, label: 'Emails', path: '/admin/emails' },
  { icon: ShieldAlert, label: 'Audit Log', path: '/admin/audit-log', superAdminOnly: true },
  { icon: AlertOctagon, label: 'Errors', path: '/admin/errors', superAdminOnly: true },
];

const mentorMenuItems = [
  { icon: LayoutDashboard, label: 'Dashboard', path: '/mentor' },
  { icon: FileCheck, label: 'Review Submissions', path: '/mentor/reviews' },
  { icon: UserCheck, label: 'My Delegates', path: '/mentor/delegates' },
  { icon: MessageSquare, label: 'Discussions', path: '/discussions' },
  { icon: Settings, label: 'Settings', path: '/settings' },
];

const seoMenuItems = [
  { icon: Newspaper, label: 'Blogs', path: '/admin/blogs' },
  { icon: CalendarDays, label: 'Events', path: '/admin/events' },
  { icon: Images, label: 'Gallery', path: '/admin/gallery' },
  { icon: Globe, label: 'Website Settings', path: '/admin/website-settings' },
];

export const Sidebar = ({ collapsed, setCollapsed }) => {
  const { user, logout, isAdmin, isMentor, isSEO, isSuperAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  // Determine menu items based on user role
  const getMenuItems = () => {
    if (isAdmin()) {
      // Hide super-admin-only items (like Audit Log) from non-super admins.
      return adminMenuItems.filter(item => !item.superAdminOnly || isSuperAdmin());
    }
    if (isMentor()) return mentorMenuItems;
    if (isSEO()) return seoMenuItems;
    return delegateMenuItems;
  };

  const menuItems = getMenuItems();

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const SidebarContent = () => (
    <>
      {/* Logo - White Background Banner */}
      <div className="flex items-center justify-center px-3 py-3 border-b border-slate-100 bg-white">
        <Link to="/" className="flex items-center justify-center">
          <img 
            src="/logo-plan4growth.png"
            alt="Plan 4 Growth"
            className={cn(
              "object-contain",
              collapsed ? "h-10 w-10" : "h-16 w-auto max-w-[180px]"
            )}
          />
        </Link>
      </div>

      {/* Menu Items */}
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {menuItems.map((item) => {
          const isActive = location.pathname === item.path || 
            (item.path !== '/dashboard' && item.path !== '/admin' && item.path !== '/mentor' && location.pathname.startsWith(item.path));
          
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setIsMobileOpen(false)}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={cn(
                'flex items-center gap-3 px-3 py-3 rounded-lg transition-colors duration-150 group',
                isActive
                  ? 'bg-[#1B3A4B] text-[#41EAD4]'
                  : 'text-slate-300 hover:bg-slate-800/50 hover:text-white'
              )}
            >
              <item.icon className={cn('w-5 h-5 flex-shrink-0', isActive && 'text-[#41EAD4]')} />
              {!collapsed && (
                <span className="font-medium">{item.label}</span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* User Section */}
      <div className="p-4 border-t border-slate-700/50">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-[#41EAD4] flex items-center justify-center text-[#0D1B2A] font-bold flex-shrink-0">
            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-slate-400 capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>
          )}
        </div>
        <Button
          variant="ghost"
          onClick={handleLogout}
          data-testid="logout-btn"
          className={cn(
            'w-full mt-4 text-slate-300 hover:text-white hover:bg-slate-800/50',
            collapsed && 'px-2'
          )}
        >
          <LogOut className="w-5 h-5" />
          {!collapsed && <span className="ml-2">Logout</span>}
        </Button>
      </div>

      {/* Collapse Button - Desktop Only */}
      <button
        onClick={() => setCollapsed(!collapsed)}
        className="hidden md:flex absolute -right-3 top-20 w-6 h-6 bg-[#0D1B2A] border border-slate-700 rounded-full items-center justify-center text-slate-400 hover:text-white hover:bg-[#1B3A4B] transition-colors"
        data-testid="sidebar-toggle"
      >
        {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
      </button>
    </>
  );

  return (
    <>
      {/* Mobile Menu Button */}
      <button
        onClick={() => setIsMobileOpen(true)}
        className="md:hidden fixed top-4 left-4 z-50 p-2 bg-[#0D1B2A] rounded-lg text-white"
        data-testid="mobile-menu-btn"
      >
        <Menu className="w-6 h-6" />
      </button>

      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      {/* Desktop Sidebar */}
      <aside
        className={cn(
          'hidden md:flex flex-col bg-[#0D1B2A] text-white fixed left-0 top-0 h-screen z-30 transition-all duration-300',
          collapsed ? 'w-20' : 'w-64'
        )}
        data-testid="sidebar"
      >
        <SidebarContent />
      </aside>

      {/* Mobile Sidebar */}
      <aside
        className={cn(
          'md:hidden flex flex-col bg-[#0D1B2A] text-white fixed left-0 top-0 h-screen z-50 w-64 transition-transform duration-300',
          isMobileOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        <SidebarContent />
      </aside>
    </>
  );
};

export default Sidebar;
